import { Trigger } from './constants';
import { convertUnicodeInStringToEmojis, getQuarterDates } from 'utils';
import { findParentNode, serializeEmailsData, serializeEventsData, generateNodeId } from './utils';
import {
  IAutomationAction,
  IAutomationSate,
  ControlTypeEnum,
  IUserWorkflowData,
  WFStatusEnum,
  NodeSide,
} from './interfaces';
import {
  AUTOMATION_NODE,
  AUTOMATION_LISTS,
  AUTOMATION_MODAL,
  AUTOMATION_EVENTS,
  CREATE_AUTOMATION,
  AUTOMATION_EMAILS,
  AUTOMATION_CONTROLS,
  AUTOMATION_WORKFLOW,
  AUTOMATION_DROP_ITEM,
  AUTOMATION_NAME_UPDATE,
  AUTOMATION_LISTS_TRIGGER,
  AUTOMATION_WORKFLOW_CLONE,
  AUTOMATION_USER_WORKFLOWS,
  AUTOMATION_EMAILS_GRAPH_DATA,
  AUTOMATION_USER_WORKFLOWS_DELETE,
  AUTOMATION_EMAILS_ANALYTICS_LIST,
  AUTOMATION_WORKFLOW_TRIGGER_LIST,
  AUTOMATION_WORKFLOW_STATUS_UPDATE,
  AUTOMATION_WORKFLOW_IF_ELSE_MODAL,
  AUTOMATION_WORKFLOW_EMAIL_PREVIEW,
  AUTOMATION_WORKFLOW_TRIGGER_EMAIL,
  AUTOMATION_SHARED_PROFILE_STATIC_DATA,
  AUTOMATION_WORKFLOW_CONDITION_EMAILS_LINKS,
} from 'constants/types/automationTypes';

const initialState: IAutomationSate = {
  emails: {
    loading: false,
    error: false,
    updateSuccess: false,
    searchTerm: '',
    filters: 'allTypes',
    showUpdate: false,
    data: [],
    totalCount: 0,
    page: 0,
    shareProfileTemplates: [],
    senderEmails: [],
    internalUser: false,
    selectedEmail: {
      id: 0,
      title: '',
      campaignId: 0,
    },
    form: {
      senderName: '',
      sendFrom: '',
      emailSubject: '',
      emailId: 0,
      designReady: false,
      versionEmailType: -1,
      campaignId: 0,
      errors: {
        senderName: false,
        sendFrom: false,
        emailSubject: false,
      },
      currentData: {
        senderName: '',
        sendFrom: '',
        emailSubject: '',
      },
    },
  },
  modals: {
    addAction: {
      // the reason of adding the "id" & "tab" is that when user want to edit the node then nodeId will go in id field and
      // type of modal (send email, add a list, if/else or delay) that should be opened will go in tab field.
      // other than edit mode "id" and "tab" field will be empty string as it will not usable.
      visible: false,
      id: '',
      tab: '',
      from: '',
    },
    shareProfileConfiguration: false,
    shareProfileTemplatesData: {
      ['']: {
        title: '',
        template_configurations: {
          ['']: {},
        },
      },
    },
    deleteAction: false,
    deleteCondition: false,
    conditionConstraint: false,
    publish: false,
    inactive: false,
  },
  events: {
    loading: false,
    error: false,
    searchTerm: '',
    data: [],
    searchedData: [],
  },
  controls: [],
  workflow: {
    loading: false,
    error: false,
    flow: {},
    defaultFlowKeys: 0,
    detail: {
      id: 0,
      name: 'New Workflow',
      nameLoading: false,
      nameError: false,
      triggerId: 0,
      triggerName: '',
      emailId: 0,
      emailName: '',
      campaignId: 0,
      versionEmailType: -1,
      status: WFStatusEnum.DRAFT,
      shareProfileUuid: '',
      errorNodes: [],
      newlyAddedNodes: [],
      triggerEmailSent: false,
      designNotReady: false,
      errorPublishNodes: [],
      listId: 0,
      listName: '',
    },
    isRecentlyPublished: false,
    toBeDeleteNode: {
      id: '',
      index: 0,
    },
    dropArea: {}, // state use to collect the data where an element will be dropped. (item data from drop function used in useDrop)
    copiedNode: {}, // to save the data of the copiedNode
    addEmails: [],
    removeEmails: [],
    shareProfileConfigurations: {
      selectedTemplate: {
        value: 'Select a template',
        label: 'Select a template',
      },
      selectedPersonalityMetrics: [],
      senderName: '',
      sendFrom: 'Select email',
      emailSubject: '',
      replyTo: 'Select email',
      initials: '',
      signature: 'Select signature',
      sendPlatform: 0,
      selectedConfigurations: {
        value: 'Select a template',
        label: 'Select a template',
      },
    },
    sharedProfileConfigLoading: false,
    shareProfileConfigurationsData: {},
    senderEmails: [],
    saveButtonDisabled: false,
    allEmailsDesignReady: true,
    isShareProfileEditing: false,
  },
  lists: {
    loading: false,
    error: false,
    searchTerm: '',
    data: [],
    searchedData: [],
    selectedLists: [],
    totalCount: 0,
    selectedListIds: [],
    newListCreatedSuccess: false,
    newListCreatedError: false,
    newlyCreatedList: {
      list_id: 0,
      list_name: '',
      id: 0,
      name: '',
      last_updated: '',
      thumbnails: [],
      created_by: '',
      last_used_by: '',
    },
  },
  userWorkflows: {
    loading: false,
    error: false,
    clonedWorkflowId: 0,
    message: '',
    success: true,
    searchTerm: '',
    filterType: '',
    data: [],
    searchedData: [],
    page: 0,
    totalCount: 0,
    totalActiveCount: 0,
  },
  reset: false,
  deleteWorkflow: {
    // state use to delete the workflows on my workflows page
    loading: false,
    error: false,
  },
  updateWorkflow: {
    // state use to update the workflow status on my workflows page
    loading: false,
    error: false,
  },
  success: {
    // use to show the success toast messages when API is successful.
    type: 'string',
    payload: {},
  },
  conditionAction: {
    // use to store conditionId and emailId
    conditionId: 0,
    conditionEmailId: 0,
  },
  emailsLinks: {
    // use to store the links of the emails
    data: {},
    loading: false,
  },
  emailPreviews: {
    data: {},
    loading: false,
    selectedEmailId: -1,
    selectedCampaignId: -1,
    textFetchSuccess: false,
  },
  emailAnalytics: {
    data: [],
    loading: false,
    emailsCount: 0,
  },
  workflowEmailGraph: {
    data: {},
    loading: false,
    selectedDateFilter: { label: '1 QTR', value: 'qtr' },
    selectedDateValue: { startDate: getQuarterDates().start_date, endDate: getQuarterDates().end_date },
  },
};

export const automationReducer = (state: IAutomationSate = initialState, action: IAutomationAction) => {
  const newState = { ...state };
  switch (action.type) {
    case AUTOMATION_MODAL.ADD_ACTION:
      newState.modals = {
        ...newState.modals,
        addAction: {
          visible: action.payload.flag,
          id: action.payload.id,
          tab: action.payload.tab,
          from: action.payload.from,
        },
      };
      return newState;

    case AUTOMATION_MODAL.SHARE_PROFILE_CONFIG:
      newState.modals = {
        ...newState.modals,
        shareProfileConfiguration: action.payload.isVisible,
      };
      return newState;
    case AUTOMATION_MODAL.DELETE_ACTION:
      newState.modals = {
        ...newState.modals,
        deleteAction: action.payload.flag,
      };
      if (action.payload.changeDeleteObject) {
        newState.workflow = {
          ...newState.workflow,
          toBeDeleteNode: {
            id: action.payload.id,
            index: action.payload.index,
          },
        };
      }
      return newState;

    case AUTOMATION_MODAL.DELETE_CONDITION:
      newState.modals = {
        ...newState.modals,
        deleteCondition: action.payload.flag,
      };
      newState.workflow = {
        ...newState.workflow,
        toBeDeleteNode: {
          id: action.payload.id,
          index: 0,
        },
      };
      return newState;

    case AUTOMATION_MODAL.CONDITION_CONSTRAINT:
      newState.modals = {
        ...newState.modals,
        conditionConstraint: action.payload.flag,
      };
      return newState;

    case AUTOMATION_MODAL.SHOW_PUBLISH_MESSAGE:
      newState.modals = {
        ...newState.modals,
        publish: action.payload.flag,
      };
      return newState;

    case AUTOMATION_MODAL.INACTIVE:
      newState.modals = {
        ...newState.modals,
        inactive: action.payload.flag,
      };
      return newState;

    // Events/Triggers Page
    case AUTOMATION_EVENTS.LOAD:
      newState.events.loading = true;
      newState.events.error = false;
      return newState;

    case AUTOMATION_EVENTS.LOAD_SUCCESS:
      newState.events.loading = false;
      newState.events.error = false;
      action.isSearched
        ? (newState.events.searchedData = serializeEventsData(action.events))
        : (newState.events.data = serializeEventsData(action.events));

      return newState;

    case AUTOMATION_EVENTS.LOAD_FAILED:
      newState.events.loading = false;
      newState.events.error = true;
      return newState;

    case CREATE_AUTOMATION.UPDATE:
      let newStatus = action.payload.status;
      if (newStatus !== WFStatusEnum.INACTIVE)
        newState.success = {
          ...newState.success,
          type: CREATE_AUTOMATION.UPDATE,
        };
      newState.workflow = {
        ...newState.workflow,
        detail: {
          ...newState.workflow.detail,
        },
        loading: false,
        error: false,
      };
      if (newStatus) newState.workflow.detail.status = newStatus;
      if (newState.modals.publish) newState.modals.publish = false;
      // to check if the email is designNotReady otherwise need to redirect to design or curated
      let designNotReady = action.payload.designNotReady;
      if (designNotReady !== undefined) newState.workflow.detail.designNotReady = action.payload.designNotReady;
      return newState;

    case CREATE_AUTOMATION.RESUME:
      newState.workflow.detail.status = action.payload.status;
      newState.workflow = {
        ...newState.workflow,
        loading: false,
      };
      newState.workflow.detail.newlyAddedNodes = [];
      return newState;

    case CREATE_AUTOMATION.UPDATE_TOASTER_MESSAGE:
      // update the message that is going to be displayed in the toaster message.
      newState.success = {
        ...newState.success,
        payload: { message: action.payload.message },
      };
      return newState;

    case CREATE_AUTOMATION.RESET_MESSAGE:
      newState.success = {
        type: '',
        payload: {},
      };
      return newState;

    case CREATE_AUTOMATION.ADD_NODE:
      const { controlName, id, controlId, controlType, side, ...params } = action.payload;
      let newId = generateNodeId(newState.workflow.defaultFlowKeys);
      if (id === Trigger) {
        // add action/condition on the top
        // if root is already been created then find the previous root node and change its id
        let obj = {};
        let children: Array<string> = [];
        // nodeId added to newlyAddedNodes if in Inactive state
        if (newState.workflow.detail.status === WFStatusEnum.INACTIVE) {
          if (!Array.isArray(newState.workflow.detail.newlyAddedNodes)) {
            newState.workflow.detail.newlyAddedNodes = []; // Initialize as an empty array
          }
          let alreadyNodes: string[] = newState.workflow.detail.newlyAddedNodes;
          if (alreadyNodes?.includes('0')) {
            newState.workflow.detail.newlyAddedNodes = [...alreadyNodes, newId];
          } else {
            newState.workflow.detail.newlyAddedNodes = [...alreadyNodes, '0'];
          }
        }
        // "zero" 0 means that its a root node
        if (newState.workflow.flow.hasOwnProperty('0')) {
          children = [newId];
          obj = {
            [newId]: {
              ...newState.workflow.flow['0'],
              detail: {
                ...newState.workflow.flow['0'].detail,
                nodeId: newId,
              },
            },
          };
          delete newState.workflow.flow[0];
        }
        if (newState.workflow.detail.errorNodes.includes('0')) {
          newState.workflow.detail.errorNodes.push(newId);
        }
        newState.workflow = {
          ...newState.workflow,
          flow: {
            '0': {
              childNodes: children,
              detail: {
                controlName,
                controlType,
                controlId,
                ...params,
                nodeId: '0',
              },
            },
            ...obj,
            ...newState.workflow.flow,
          },
          detail: {
            ...newState.workflow.detail,
            errorNodes: newState.workflow.detail.errorNodes.filter((node) => node !== '0'),
          },
        };
      } else {
        // action/condition on any level other than root
        let parentChildren: Array<string> = [];
        let childChildren: Array<string> = [];
        // nodeId added to newlyAddedNodes if in Inactive state
        if (newState.workflow.detail.status === WFStatusEnum.INACTIVE) {
          if (!Array.isArray(newState.workflow.detail.newlyAddedNodes)) {
            newState.workflow.detail.newlyAddedNodes = []; // Initialize as an empty array
          }
          let alreadyNodes: string[] = newState.workflow.detail.newlyAddedNodes;
          newState.workflow.detail.newlyAddedNodes = [...alreadyNodes, newId];
        }
        if (newState.workflow.flow[id]?.detail?.controlType === ControlTypeEnum.CONDITION) {
          let subChildren = newState.workflow.flow[id]?.childNodes;
          if (side === NodeSide.LEFT) {
            parentChildren = [newId, subChildren?.[1] || '']; // if null then put empty string
            subChildren[0] !== '' && childChildren.push(subChildren?.[0]);
          } else {
            // on left side we're putting the 'yes' child and on the right side
            // we're putting the 'no' child. So if user put an action/condition on the right side
            // we added empty string on the first place
            let items = subChildren.length === 0 ? '' : subChildren[0];
            parentChildren = [items, newId];
            childChildren = [subChildren?.[1] || '']; // if null then put empty string
          }
        } else {
          parentChildren = [newId];
          let newChildrens = newState.workflow.flow[id]?.childNodes || [];
          childChildren = [...newChildrens];
        }
        newState.workflow = {
          ...newState.workflow,
          flow: {
            ...newState.workflow.flow,
            [newId]: {
              childNodes: childChildren.filter((item) => item),
              detail: {
                controlName,
                controlType,
                controlId,
                ...params,
                nodeId: newId,
              },
            },
            [id]: {
              ...newState.workflow.flow[id],
              childNodes: parentChildren,
            },
          },
        };
      }
      return newState;

    case AUTOMATION_NODE.DELETE:
      const { id: nodeId, index: nodePlace } = action.payload;
      // nodePlace to keep
      let newWorkFlow = { ...newState.workflow.flow };
      let node = { ...newWorkFlow[nodeId] };
      let nodeChildren = [...node?.childNodes];
      delete newWorkFlow[nodeId]; // delete the node
      let newlyAddedNodesTemp = newState.workflow.detail.newlyAddedNodes;
      let errorNodeAtStart = false;
      if (nodeId === '0' && nodeChildren.length) {
        let data = newWorkFlow[nodeChildren[nodePlace]];
        const errorNodes = newState.workflow.detail.errorNodes;
        if (nodeChildren.some((node) => errorNodes.includes(node)) && data.detail.controlType === 'CONDITION') {
          errorNodeAtStart = true;
        }
        // reomving node from newlyAddedNodes
        if (newlyAddedNodesTemp) {
          if (newlyAddedNodesTemp.includes(nodeChildren?.[nodePlace])) {
            let deleteIndex = 1 - nodePlace;
            let idToBeDeleted = nodeChildren?.[deleteIndex];
            newlyAddedNodesTemp = newlyAddedNodesTemp.filter((id) => id !== idToBeDeleted);
          } else {
            newlyAddedNodesTemp = newlyAddedNodesTemp.filter((id) => id !== '0');
          }
        }
        if (data) {
          newWorkFlow = {
            '0': {
              ...data,
              detail: {
                ...data.detail,
                nodeId: '0',
              },
            },
            ...newWorkFlow,
          };
        }
        nodeChildren.forEach((child) => {
          delete newWorkFlow[child]; // delete the child node because it become the root node
        });
      } else if (nodeId !== '0') {
        // reomving node from newlyAddedNodes
        if (newlyAddedNodesTemp?.includes(nodeId)) {
          newlyAddedNodesTemp = newlyAddedNodesTemp.filter((id) => id !== nodeId);
        }
        let parentkey = findParentNode(nodeId, { ...newWorkFlow, [nodeId]: node });
        const parentChildrenObject = newWorkFlow[parentkey];
        let children = [];
        if (node?.detail?.controlType === ControlTypeEnum.CONDITION && nodeChildren.length) {
          // remove the opposite of nodePlace Node from the workflow
          let deleteIndex = 1 - nodePlace;
          let id = nodeChildren?.[deleteIndex];
          if (id || id ==='') { //Added a check to ensure that if the node condition "true" is empty then whole condition childs wont delete id ='';
            deleteIndex === 0 ? nodeChildren.shift() : nodeChildren.pop();
            delete newWorkFlow[id];
          }
        }
        if (parentChildrenObject?.detail?.controlType === ControlTypeEnum.CONDITION) {
          let index = parentChildrenObject?.childNodes.indexOf(nodeId);
          children = [...parentChildrenObject?.childNodes];
          children[index] = nodeChildren?.[0] || ''; // if null then put empty string
        } else {
          children = nodeChildren;
        }

        newWorkFlow = {
          ...newWorkFlow,
          [parentkey]: {
            ...newWorkFlow[parentkey],
            childNodes: children,
          },
        };
      }

      newState.workflow.detail.newlyAddedNodes = newlyAddedNodesTemp;

      newState.workflow = {
        ...newState.workflow,
        flow: newWorkFlow,
        toBeDeleteNode: {
          id: '',
          index: 0,
        },
        detail: {
          ...newState.workflow.detail,
          errorNodes: newState.workflow.detail.errorNodes.filter((item: string) => item !== nodeId),
        },
      };
      errorNodeAtStart && newState.workflow.detail.errorNodes.push('0');
      newState.modals = {
        ...newState.modals,
        deleteCondition: false,
        deleteAction: false,
      };

      return newState;

    case AUTOMATION_NODE.COPY:
      let copiedNode = newState.workflow.flow[action.payload]?.detail;
      newState.workflow = {
        ...newState.workflow,
        copiedNode: copiedNode,
      };
      newState.success = {
        type: AUTOMATION_NODE.COPY,
        payload: {
          message: 'Copied',
        },
      };
      return newState;

    case AUTOMATION_NODE.CLEAR_COPY:
      newState.workflow = {
        ...newState.workflow,
        copiedNode: {},
      };
      return newState;

    case AUTOMATION_NODE.MOVE:
      const { nodeId: moveNodeId, id: dropNodeId, side: nodeSide } = action.payload; // nodeId is the id of the node that we have to move
      // id is where the movedNode will be become child.
      let dropNode = { ...newState.workflow.flow[dropNodeId] };
      let moveNode = { ...newState.workflow.flow[moveNodeId] };
      let parentNodeKey = findParentNode(moveNodeId, newState.workflow.flow);
      let newFlow = null;
      if (dropNodeId === Trigger) {
        const rootNode = { ...newState.workflow.flow['0'] };
        const newId = generateNodeId(newState.workflow.defaultFlowKeys);
        newFlow = {
          ...newState.workflow.flow,
          '0': {
            ...moveNode,
            childNodes: [newId],
            detail: {
              ...moveNode.detail,
              nodeId: '0',
            },
          },
          [newId]: {
            childNodes: parentNodeKey === '0' ? [...moveNode?.childNodes] : [...rootNode?.childNodes],
            detail: {
              ...rootNode.detail,
              nodeId: newId,
            },
          },
        };
        if (parentNodeKey !== '0') {
          newFlow = {
            ...newFlow,
            [parentNodeKey]: {
              ...newState.workflow.flow[parentNodeKey],
              childNodes: [...moveNode?.childNodes],
            },
          };
        }
        newState.workflow = {
          ...newState.workflow,
          flow: newFlow,
        };
        delete newState.workflow.flow[moveNodeId];
      } else {
        let dropNodeChildren = null;
        let moveNodeChildren = null;
        // if the node on which the user eant to drop another node is condition then use the following code
        if (dropNode.detail?.controlType === ControlTypeEnum.CONDITION) {
          let dropNodeLeftChild = dropNode?.childNodes?.[0];
          let dropNodeRightChild = dropNode?.childNodes?.[1];
          dropNodeChildren = NodeSide.LEFT ? [moveNodeId, dropNodeRightChild] : [dropNodeLeftChild, moveNodeId];
          moveNodeChildren = NodeSide.LEFT ? [dropNodeLeftChild] : [dropNodeRightChild];
        } else {
          dropNodeChildren = [moveNodeId];
          moveNodeChildren = [...dropNode?.childNodes];
        }
        newState.workflow = {
          ...newState.workflow,
          flow: {
            ...newState.workflow.flow,
            [dropNodeId]: {
              ...dropNode,
              childNodes: dropNodeChildren,
            },
            [moveNodeId]: {
              ...moveNode,
              childNodes: moveNodeChildren,
            },
            [parentNodeKey]: {
              ...newState.workflow.flow[parentNodeKey],
              childNodes: [...moveNode?.childNodes],
            },
          },
        };
      }
      return newState;

    case AUTOMATION_NODE.EDIT:
      const { id: nId, data } = action.payload;
      newState.workflow = {
        ...newState.workflow,
        flow: {
          ...newState.workflow.flow,
          [nId]: {
            ...newState.workflow.flow[nId],
            detail: {
              ...newState.workflow.flow[nId]?.detail,
              ...data,
            },
          },
        },
      };
      return newState;

    case AUTOMATION_NODE.ERROR:
      const errorNodeId = action.payload.id;
      newState.workflow = {
        ...newState.workflow,
        flow: {
          ...newState.workflow.flow,
          [errorNodeId]: {
            ...newState.workflow.flow[errorNodeId],
            detail: {
              ...newState.workflow.flow[errorNodeId].detail,
              error: action.payload.isError,
            },
          },
        },
      };
      return newState;

    case CREATE_AUTOMATION.SAVE:
      const {
        id: wId,
        name,
        email_id,
        email_name,
        status,
        trigger_id,
        trigger_name,
        campaign_id,
        sp_uuid,
        list_id,
        list_name,
      } = action.payload;

      newState.workflow = {
        ...newState.workflow,
        loading: false,
        error: false,
        detail: {
          ...newState.workflow.detail,
          id: wId,
          name,
          nameLoading: false,
          nameError: false,
          triggerId: trigger_id,
          triggerName: trigger_name,
          emailId: email_id,
          emailName: email_name,
          campaignId: campaign_id,
          status,
          shareProfileUuid: sp_uuid,
          listId: list_id,
          listName: list_name,
        },
      };
      return newState;

    case CREATE_AUTOMATION.LOADING:
      newState.workflow = {
        ...newState.workflow,
        loading: true,
        error: false,
      };
      return newState;

    case CREATE_AUTOMATION.FAIL:
      newState.workflow = {
        ...newState.workflow,
        loading: false,
        error: true,
      };
      return newState;

    // Select Email Page
    case AUTOMATION_EMAILS.LOAD:
      newState.emails.loading = true;
      newState.emails.error = false;
      if (action.payload.resetPage) newState.emails.page = 0;
      return newState;

    case AUTOMATION_EMAILS.LOAD_SUCCESS:
      newState.emails.loading = false;
      newState.emails.error = false;
      newState.emails.totalCount = action.totalCount;
      newState.emails.page = newState.emails.page + 1;
      newState.emails.internalUser = action.payload.internalUser;
      newState.emails.data = action.paginatedCall
        ? [...newState.emails.data, ...serializeEmailsData(action.emails)]
        : [...serializeEmailsData(action.emails)];
      return newState;

    case AUTOMATION_EMAILS.LOAD_FAILED:
      newState.emails.loading = false;
      newState.emails.error = true;
      return newState;

    case AUTOMATION_EMAILS.SET_FILTERS:
      newState.emails.filters = action.payload;
      return newState;

    case AUTOMATION_EMAILS.SET_SEARCH_TERM:
      newState.emails.searchTerm = action.payload;
      return newState;

    case AUTOMATION_EMAILS.RESET:
      newState.emails.data = [];
      newState.emails.totalCount = 0;
      return newState;

    case AUTOMATION_EMAILS.SET_SELECTED_EMAIL_ID:
      newState.emails = {
        ...newState.emails,
        selectedEmail: {
          id: action.payload.emailId,
          title: action.payload.emailTitle,
          campaignId: action.payload.campaignId,
        },
      };
      return newState;

    case AUTOMATION_EMAILS.SET_SHOW_UPDATE:
      newState.emails = {
        ...newState.emails,
        showUpdate: action.payload,
      };
      return newState;

    case AUTOMATION_EMAILS.SET_FORM:
      newState.emails = {
        ...newState.emails,
        form: {
          senderName: action.payload.senderName,
          sendFrom: action.payload.sendFrom,
          emailSubject: action.payload.emailSubject,
          emailId: action.payload.emailId,
          designReady: action.payload.designReady,
          versionEmailType: action.payload.versionEmailType,
          campaignId: action.payload.campaignId,
          errors: action.payload.errors,
          currentData: {
            senderName: action.payload.senderName,
            sendFrom: action.payload.sendFrom,
            emailSubject: action.payload.emailSubject,
          },
        },
      };
      return newState;

    case AUTOMATION_EMAILS.SET_UPDATE_SUCCESS:
      newState.emails = {
        ...newState.emails,
        updateSuccess: action.payload,
      };
      return newState;

    case AUTOMATION_EMAILS.RESET_FORM_DATA:
      newState.emails = {
        ...newState.emails,
        form: {
          ...newState.emails.form,
          senderName: action.payload.senderName,
          sendFrom: action.payload.sendFrom,
          emailSubject: action.payload.emailSubject,
        },
      };
      return newState;

    case AUTOMATION_EMAILS.UPDATE_FORM:
      newState.emails = {
        ...newState.emails,
        form: {
          ...newState.emails.form,
          [action.payload.name]: action.payload.value,
          errors: {
            ...newState.emails.form.errors,
            [action.payload.name]: action.payload.error,
          },
        },
      };
      return newState;

    case AUTOMATION_EMAILS.UPDATE_FORM_CURRENT_DATA:
      newState.emails = {
        ...newState.emails,
        form: {
          ...newState.emails.form,
          currentData: {
            senderName: action.payload.senderName,
            sendFrom: action.payload.sendFrom,
            emailSubject: action.payload.emailSubject,
          },
        },
      };
      return newState;

    case AUTOMATION_EMAILS.UPDATE_EMAIL_DATA:
      const updateEmailIndex = newState.emails.data.findIndex((email) => email.emailId === action.payload.emailId);
      const updatedEmails = [...newState.emails.data];
      updatedEmails[updateEmailIndex] = {
        ...updatedEmails[updateEmailIndex],
        subject: action.payload.subject,
        sendFrom: action.payload.sendFrom,
        senderName: action.payload.senderName,
        versionEmailType: action.payload.versionEmailType,
        designReady: action.payload.designReady,
      };

      newState.emails = {
        ...newState.emails,
        data: updatedEmails,
      };
      return newState;

    case AUTOMATION_EMAILS.SELECTED_ON_TOP:
      let emails = action.payload.data;
      const selectedEmail = emails.filter((email: any) => email.emailId === action.payload.emailId);
      emails = emails.filter((email: any) => email.emailId !== action.payload.emailId);
      newState.emails.data = [...selectedEmail, ...emails];
      return newState;

    case AUTOMATION_DROP_ITEM.SAVE:
      newState.workflow = {
        ...newState.workflow,
        dropArea: action.payload,
      };
      return newState;

    case AUTOMATION_DROP_ITEM.CLEAR:
      newState.workflow = {
        ...newState.workflow,
        dropArea: {},
      };
      return newState;

    // Workflow name update
    case AUTOMATION_NAME_UPDATE.LOAD:
      newState.workflow = {
        ...newState.workflow,
        detail: {
          ...newState.workflow.detail,
          nameLoading: true,
          nameError: false,
        },
      };
      return newState;

    case AUTOMATION_NAME_UPDATE.SUCCESS:
      newState.workflow = {
        ...newState.workflow,
        detail: {
          ...newState.workflow.detail,
          name: action.payload,
          nameLoading: false,
          nameError: false,
        },
      };
      return newState;

    case AUTOMATION_NAME_UPDATE.FAIL:
      newState.workflow = {
        ...newState.workflow,
        detail: {
          ...newState.workflow.detail,
          nameLoading: false,
          nameError: true,
        },
      };
      return newState;

    case AUTOMATION_WORKFLOW.SHARE_PROFILE_CONFIG_DATA:
      newState.workflow = {
        ...newState.workflow,
        shareProfileConfigurationsData: action.payload.shareProfileConfigurationsData,
        senderEmails: action.payload.senderEmails,
      };
      return newState;

    // Add Lists Modal
    case AUTOMATION_LISTS.LOAD:
      newState.lists.loading = true;
      newState.lists.error = false;
      return newState;

    case AUTOMATION_LISTS.LOAD_SUCCESS:
      newState.lists.loading = false;
      newState.lists.error = false;
      newState.lists.data = [...newState.lists.data, ...action.lists];
      newState.lists.totalCount = action.totalCount;
      return newState;

    case AUTOMATION_LISTS.SET_SEARCH_DATA:
      newState.lists = {
        ...newState.lists,
        loading: false,
        error: false,
        searchedData: action.reset ? [...action.lists] : [...newState.lists.searchedData, ...action.lists],
        totalCount: action.totalCount,
      };
      return newState;

    case AUTOMATION_LISTS.LOAD_FAILED:
      newState.lists.loading = false;
      newState.lists.error = true;
      return newState;

    case AUTOMATION_LISTS.RESET:
      newState.lists.loading = false;
      newState.lists.error = false;
      newState.lists.data = [];
      newState.lists.totalCount = 0;
      return newState;

    case AUTOMATION_LISTS.SET_SEARCH_TERM:
      newState.lists.searchTerm = action.payload;
      return newState;

    case AUTOMATION_LISTS.RESET_SEARCH_DATA:
      newState.lists = {
        ...newState.lists,
        searchedData: [],
        totalCount: 0,
      };
      return newState;

    case AUTOMATION_LISTS.NEW_LIST_CREATED:
      newState.lists.data = [action.newlyCreatedListInWorkflow, ...newState.lists.data];
      if (action.payload) {
        newState.lists.searchedData = [action.newlyCreatedListInWorkflow, ...newState.lists.searchedData];
      }
      newState.lists.newlyCreatedList = action.newlyCreatedListInWorkflow;
      return newState;

    case AUTOMATION_LISTS.NEW_LIST_DATA_RESET:
      newState.lists.newlyCreatedList = {
        list_id: 0,
        list_name: '',
        id: 0,
        name: '',
        last_updated: '',
        thumbnails: [],
        created_by: '',
        last_used_by: '',
      };
      return newState;

    case AUTOMATION_LISTS.SET_SELECTED_LIST_IDS:
      newState.lists.selectedListIds = action.payload.list_ids;
      return newState;

    case AUTOMATION_LISTS.SET_SELECTED_LISTS:
      newState.lists.selectedLists = action.payload.lists;
      return newState;

    case AUTOMATION_LISTS.CREATED_SUCCESS_STATE:
      newState.lists.newListCreatedSuccess = action.payload;
      return newState;

    case AUTOMATION_LISTS.CREATED_ERROR_STATE:
      newState.lists.newListCreatedError = action.payload;
      return newState;

    case AUTOMATION_CONTROLS.SAVE:
      newState.controls = action.payload;
      return newState;

    case AUTOMATION_WORKFLOW.UPDATE_SAVE_BUTTON_STATE:
      newState.workflow.saveButtonDisabled = action.payload;
      return newState;

    case AUTOMATION_WORKFLOW.UPDATE_ALL_EMAILS_DESIGN_READY:
      newState.workflow.allEmailsDesignReady = action.payload;
      return newState;

    case AUTOMATION_WORKFLOW.EDITING_SHARE_PROFILE:
      newState.workflow.isShareProfileEditing = action.payload;
      return newState;

    case AUTOMATION_WORKFLOW.RESET:
      newState.workflow = {
        loading: false,
        error: false,
        flow: {},
        defaultFlowKeys: 0,
        detail: {
          id: 0,
          name: 'New Workflow',
          nameLoading: false,
          nameError: false,
          triggerId: 0,
          triggerName: '',
          emailId: 0,
          emailName: '',
          campaignId: 0,
          versionEmailType: -1,
          status: WFStatusEnum.DRAFT,
          shareProfileUuid: '',
          errorNodes: [],
          newlyAddedNodes: [],
          triggerEmailSent: false,
          errorPublishNodes: [],
          listId: 0,
          listName: '',
        },
        isRecentlyPublished: false,
        toBeDeleteNode: {
          id: '',
          index: 0,
        },
        dropArea: {
          ...newState.workflow.dropArea,
        },
        copiedNode: {},
        addEmails: [],
        removeEmails: [],
        shareProfileConfigurations: {
          selectedTemplate: {
            value: 'Select a template',
            label: 'Select a template',
          },
          selectedPersonalityMetrics: [],
          senderName: '',
          sendFrom: 'Select email',
          emailSubject: '',
          replyTo: 'Select email',
          initials: '',
          signature: 'Select signature',
          sendPlatform: 0,
          selectedConfigurations: {
            value: 'Select a template',
            label: 'Select a template',
          },
        },
        sharedProfileConfigLoading: false,
        shareProfileConfigurationsData: {},
        senderEmails: [],
        saveButtonDisabled: false,
        allEmailsDesignReady: true,
        isShareProfileEditing: false,
      };
      return newState;

    case AUTOMATION_WORKFLOW.LOAD:
      newState.workflow = {
        ...newState.workflow,
        loading: true,
        error: false,
      };
      return newState;

    case AUTOMATION_WORKFLOW.LOAD_SUCCESS:
      const {
        id: workflowId,
        workflow,
        name: workflowName,
        email_id: emailId,
        email_name: emailName,
        email_type: versionEmailType,
        status: workflowStatus,
        trigger_id: triggerId,
        trigger_name: triggerName,
        campaign_id: campaignId,
        sp_uuid: shareProfileUuid,
        error_nodes: errorNodes,
        trigger_email_sent,
        error_publish_nodes: errorPublishNodes,
        list_id: listId,
        list_name: listName,
      } = action.payload;

      let parsedWF = workflow ? JSON.parse(workflow) : {};
      let newlyAddedNodes = parsedWF['newlyAddedNodes'];
      delete parsedWF['newlyAddedNodes'];
      delete parsedWF['extras'];
      let arr = Object.keys(parsedWF).map((key) => parseInt(key)); //convert keys into number
      newState.workflow = {
        ...newState.workflow,
        loading: false,
        error: false,
        flow: parsedWF,
        defaultFlowKeys: arr.length ? Math.max(...arr) : 0, // get the max number
        detail: {
          id: workflowId,
          name: workflowName,
          nameLoading: false,
          nameError: false,
          triggerId,
          triggerName,
          emailId,
          emailName,
          versionEmailType,
          campaignId,
          status: workflowStatus,
          shareProfileUuid: shareProfileUuid,
          errorNodes,
          newlyAddedNodes,
          triggerEmailSent: trigger_email_sent,
          errorPublishNodes,
          listId,
          listName,
        },
        isRecentlyPublished: false,
        toBeDeleteNode: {
          id: '',
          index: 0,
        },
        // dropArea: {},
        copiedNode: {},
        addEmails: [],
        removeEmails: [],
      };
      return newState;
    case AUTOMATION_WORKFLOW.UPDATE_SAME_EMAIL_NODES_TITLE:
      const { emailSubject: updatedEmailName, emailId: updatedEmailId } = action.payload;
      Object.values(newState.workflow.flow).forEach((wf: any) => {
        if (wf.detail.controlType === 'ACTION') {
          if (wf.detail.emailId === updatedEmailId) {
            wf.detail.emailName = updatedEmailName;
            wf.detail.nodeTitle = ': ' + updatedEmailName;
          }
        } else if (wf.detail.controlType === 'CONDITION') {
          wf.detail.conditions.forEach((con: any) => {
            if (con.emailId === updatedEmailId) {
              con.emailName = updatedEmailName;
            }
          });
        }
      });
      return newState;
    case AUTOMATION_WORKFLOW.LOAD_FAILED:
      newState.workflow = {
        ...newState.workflow,
        loading: false,
        error: true,
      };
      return newState;

    case AUTOMATION_WORKFLOW.RESET_EMAILS:
      newState.workflow = {
        ...newState.workflow,
        addEmails: [],
        removeEmails: [],
      };
      return newState;

    case AUTOMATION_WORKFLOW.ADD_EMAIL:
      let addedEmailId = action.payload;
      let newAddEmails = [...newState.workflow.addEmails, addedEmailId];
      let isIndeleteEmails = newState.workflow.removeEmails.includes(addedEmailId);
      if (isIndeleteEmails) {
        newState.workflow = {
          ...newState.workflow,
          removeEmails: newState.workflow.removeEmails.filter((item) => item !== addedEmailId),
        };
      }
      newState.workflow = {
        ...newState.workflow,
        addEmails: newAddEmails,
      };
      return newState;

    case AUTOMATION_WORKFLOW.REMOVE_EMAIL:
      let deletedEmailId = action.payload;
      let isInAddEmails = newState.workflow.addEmails.includes(deletedEmailId);
      // if email id is in addEmails then only means from that array because its mean user deleted the added email
      if (isInAddEmails) {
        newState.workflow = {
          ...newState.workflow,
          addEmails: newState.workflow.addEmails.filter((item) => item !== deletedEmailId),
        };
      } else {
        let newRemoveEmails = [...newState.workflow.removeEmails, deletedEmailId];
        newState.workflow = {
          ...newState.workflow,
          removeEmails: newRemoveEmails,
        };
      }
      return newState;

    case AUTOMATION_WORKFLOW.UPDATE_RECENTLY_PUBLISHED:
      newState.workflow = {
        ...newState.workflow,
        isRecentlyPublished: action.payload,
      };
      return newState;

    case AUTOMATION_WORKFLOW.UPDATE_ERROR_NODES_LIST:
      newState.workflow.detail = {
        ...newState.workflow.detail,
        errorNodes: newState.workflow.detail.errorNodes.filter((item: string) => item !== action.payload),
      };
      return newState;

    case AUTOMATION_WORKFLOW.UPDATE_ERROR_PUBLISH_NODES_LIST:
      newState.workflow.detail = {
        ...newState.workflow.detail,
        errorPublishNodes: newState.workflow.detail.errorPublishNodes.filter((item: string) => item !== action.payload),
      };
      return newState;

    case AUTOMATION_WORKFLOW.EMPTY_NEWLY_ADDED_NODES:
      newState.workflow.detail = {
        ...newState.workflow.detail,
        newlyAddedNodes: [],
      };
      return newState;

    case AUTOMATION_USER_WORKFLOWS.LOAD:
      newState.userWorkflows.loading = true;
      newState.userWorkflows.error = false;
      if (action.payload.offset === 0) newState.userWorkflows.page = 0;
      return newState;

    case AUTOMATION_USER_WORKFLOWS.LOAD_SUCCESS:
      newState.userWorkflows.loading = false;
      newState.userWorkflows.error = false;
      newState.userWorkflows.page = newState.userWorkflows.page + 1;
      newState.userWorkflows.totalCount = action.totalCount;
      if (action.offset === 0) {
        newState.userWorkflows.totalActiveCount = action.totalActiveCount;
      }
      action.isSearched
        ? (newState.userWorkflows.searchedData = action.paginatedCall
            ? [...newState.userWorkflows.searchedData, ...action.userWorkflows]
            : [...action.userWorkflows])
        : (newState.userWorkflows.data = action.paginatedCall
            ? [...newState.userWorkflows.data, ...action.userWorkflows]
            : [...action.userWorkflows]);
      return newState;

    case AUTOMATION_USER_WORKFLOWS.INCREMENT_ACTIVE_COUNT:
      newState.userWorkflows.totalActiveCount += 1;
      return newState;
    
    case AUTOMATION_USER_WORKFLOWS.DECREMENT_ACTIVE_COUNT:
      newState.userWorkflows.totalActiveCount -= 1;
      return newState;
      
    case AUTOMATION_USER_WORKFLOWS.INCREMENT_TOTAL_COUNT:
      newState.userWorkflows.totalCount += 1;
      return newState;
          
    case AUTOMATION_USER_WORKFLOWS.UPDATE_USERS_WORKFLOW_STATUS:
      const { status: workflow_status, workflowId: workflow_id } = action.payload;
      const isSearched = !!newState.userWorkflows.searchTerm || !!newState.userWorkflows.filterType;
      if (isSearched) {
        newState.userWorkflows.searchedData[
          newState.userWorkflows.searchedData.findIndex((wf) => wf.id === workflow_id)
        ].status = workflow_status;
      } else {
        if (newState.userWorkflows.data?.length)
          newState.userWorkflows.data[newState.userWorkflows.data.findIndex((wf) => wf.id === workflow_id)].status =
            workflow_status;
      }
      return newState;
    case AUTOMATION_USER_WORKFLOWS.UPDATE_USERS_WORKFLOW_NAME:
      const { name: workflow_name, workflowId: workflow_id_ } = action.payload;
      newState.userWorkflows.data[newState.userWorkflows.data.findIndex((wf) => wf.id === workflow_id_)].name =
        workflow_name;
      return newState;
    case AUTOMATION_USER_WORKFLOWS.SET_SEARCH_TERM:
      newState.userWorkflows.searchTerm = action.payload;
      return newState;

    case AUTOMATION_USER_WORKFLOWS.SET_FILTER_TYPE:
      newState.userWorkflows.filterType = action.payload;
      return newState;

    case AUTOMATION_USER_WORKFLOWS.ERROR_SEARCH_TERM:
      newState.userWorkflows.message = action.payload.message;
      newState.userWorkflows.success = action.payload.success;
      return newState;

    case AUTOMATION_USER_WORKFLOWS.ERROR_SEARCH_TERM_LOADING:
      newState.userWorkflows.loading = false;
      newState.emails.loading = false;
      newState.lists.loading = false;
      return newState;

    case AUTOMATION_USER_WORKFLOWS.LOAD_FAILED:
      newState.userWorkflows.loading = false;
      newState.userWorkflows.error = true;
      return newState;

    case AUTOMATION_USER_WORKFLOWS.RESET:
      newState.userWorkflows = {
        loading: false,
        error: false,
        clonedWorkflowId: 0,
        message: '',
        success: true,
        searchTerm: '',
        filterType: '',
        data: [],
        searchedData: [],
        page: 0,
        totalCount: 0,
        totalActiveCount: 0,
      };
      return newState;

    case AUTOMATION_USER_WORKFLOWS_DELETE.LOAD:
      newState.deleteWorkflow.loading = true;
      newState.deleteWorkflow.error = false;
      return newState;

    case AUTOMATION_USER_WORKFLOWS_DELETE.LOAD_SUCCESS:

      const deleteWorkflowIds: number[] = action.payload;
      const updatedWorkflowsData = newState.userWorkflows.data.filter(
        (workflow: IUserWorkflowData) => !deleteWorkflowIds.includes(workflow.id),
      );
    
      const updatedWorkflowsSearchedData = newState.userWorkflows.searchedData.filter(
        (workflow: IUserWorkflowData) => !deleteWorkflowIds.includes(workflow.id),
      );
    
      newState.userWorkflows.data = updatedWorkflowsData;
      newState.userWorkflows.searchedData = updatedWorkflowsSearchedData;
      newState.deleteWorkflow.loading = false;
      newState.deleteWorkflow.error = false;
      if(action.searchTerm?.length || action.filterType?.length){
        newState.userWorkflows.totalCount =  newState.userWorkflows.totalCount - deleteWorkflowIds?.length;
      }else{
        newState.userWorkflows.totalCount = action.totalCount;
      }
      return newState;

    case AUTOMATION_USER_WORKFLOWS_DELETE.LOAD_FAILED:
      newState.deleteWorkflow.loading = false;
      newState.deleteWorkflow.error = true;
      return newState;

    // Update Workflow Status
    case AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD:
      newState.updateWorkflow.loading = true;
      newState.updateWorkflow.error = false;
      return newState;

    case AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD_SUCCESS:
      const { workflowId: idToUpdate, workflowStatus: updatedStatus } = action.payload;
      newState.updateWorkflow.loading = false;
      newState.updateWorkflow.error = false;
      newState.userWorkflows.data = newState.userWorkflows.data.map((workflow: IUserWorkflowData) => {
        if (workflow.id === idToUpdate) {
          workflow.status = updatedStatus;
        }
        return workflow;
      });
      newState.userWorkflows.searchedData = newState.userWorkflows.searchedData.map((workflow: IUserWorkflowData) => {
        if (workflow.id === idToUpdate) {
          workflow.status = updatedStatus;
        }
        return workflow;
      });

      if(['active','inactive'].includes(newState.userWorkflows.filterType)){
        newState.userWorkflows.searchedData = newState.userWorkflows.searchedData.filter(obj => obj.status === newState?.userWorkflows?.filterType )
        newState.userWorkflows.totalCount -= 1
      }

      // newState.userWorkflows.searchedData.forEach((workflow: IUserWorkflowData) => {//Update logic so that search array not update on the updating the active or inactive status
      //   if (workflow.id === idToUpdate) {
      //     newState.userWorkflows.totalCount -= 1;
      //   }
      // });

      if (newState.workflow.detail.newlyAddedNodes?.length) {
        newState.workflow.detail.newlyAddedNodes = [];
      }
      return newState;

    case AUTOMATION_WORKFLOW_STATUS_UPDATE.LOAD_FAILED:
      newState.updateWorkflow.loading = false;
      newState.updateWorkflow.error = true;
      return newState;

    case AUTOMATION_WORKFLOW_STATUS_UPDATE.ERROR_UPDATE:
      newState.updateWorkflow.error = false;
      return newState;

    case AUTOMATION_WORKFLOW_IF_ELSE_MODAL.SET_CONDITION_EMAIL:
      const { conditionId, conditionEmailId } = action.payload;
      newState.conditionAction = { conditionId, conditionEmailId };
      return newState;

    case AUTOMATION_WORKFLOW.SHARE_PROFILE_CONFIG_UPDATE:
      newState.workflow = {
        ...newState.workflow,
        shareProfileConfigurations: action.shareProfileConfigurations,
      };
      return newState;

    case AUTOMATION_SHARED_PROFILE_STATIC_DATA.LOAD:
      newState.workflow = {
        ...newState.workflow,
        sharedProfileConfigLoading: true,
      };
      return newState;

    case AUTOMATION_SHARED_PROFILE_STATIC_DATA.SUCCESS:
      let configData = action.payload.shareProfileConfigurationsData;
      if (configData) {
        let signatures = action.payload.shareProfileConfigurationsData.signature;
        let dataObject = signatures.data;
        let signaturesData: any = [];
        if (typeof dataObject === 'object') {
          Object.entries(dataObject).map((obj) => {
            signaturesData.push({ id: obj[0], title: obj[1] });
          });
        }
        signatures.data = signaturesData;
        configData.signature = signatures;
      }
      newState.workflow = {
        ...newState.workflow,
        shareProfileConfigurationsData: configData,
        senderEmails: action.payload.senderEmails,
        sharedProfileConfigLoading: false,
      };
      if (Object.keys(action?.payload.shareProfileTemplates)?.length > 0) {
        let templates = action.payload.shareProfileTemplates;
        let templatesData: any = [];
        if (typeof templatesData === 'object') {
          Object.entries(templates).map((temp) => {
            templatesData.push({ value: temp[0], label: temp[1] });
          });
        }
        newState.emails.shareProfileTemplates = templatesData;
      }
      newState.emails.senderEmails = action.payload.senderEmails;
      newState.modals.shareProfileTemplatesData = { ...action.payload.shareProfileTemplatesConfigurationData };
      return newState;

    case AUTOMATION_SHARED_PROFILE_STATIC_DATA.FAIL:
      newState.workflow = {
        ...newState.workflow,
        sharedProfileConfigLoading: false,
      };
      return newState;
    case AUTOMATION_WORKFLOW_CONDITION_EMAILS_LINKS.LOAD:
      newState.emailsLinks.loading = true;
      return newState;

    case AUTOMATION_WORKFLOW_CONDITION_EMAILS_LINKS.LOAD_SUCCESS:
      newState.emailsLinks.loading = false;
      newState.emailsLinks.data = action.payload;
      return newState;

    case AUTOMATION_WORKFLOW_CONDITION_EMAILS_LINKS.LOAD_FAILED:
      newState.emailsLinks.loading = false;
      return newState;

    case AUTOMATION_WORKFLOW_CONDITION_EMAILS_LINKS.RESET:
      newState.emailsLinks.loading = false;
      newState.emailsLinks.data = {};
      return newState;

    case AUTOMATION_WORKFLOW_EMAIL_PREVIEW.LOAD:
      newState.emailPreviews.loading = true;
      newState.emailPreviews.selectedEmailId = action.selectedEmailId;
      newState.emailPreviews.selectedCampaignId = action.selectedCampaignId;
      return newState;

    case AUTOMATION_WORKFLOW_EMAIL_PREVIEW.LOAD_SUCCESS:
      newState.emailPreviews.loading = false;
      if (newState.emailPreviews.data[action.selectedEmailId]) {
        newState.emailPreviews.data[action.selectedEmailId] = action.payload;
      } else {
        newState.emailPreviews.data[action.selectedEmailId] = action.payload;
      }
      return newState;

    case AUTOMATION_WORKFLOW_EMAIL_PREVIEW.LOAD_FAILED:
      newState.emailPreviews.loading = false;
      return newState;

    case AUTOMATION_WORKFLOW_EMAIL_PREVIEW.LOAD_TEXT_PREVIEW_SUCCESS:
      newState.emailPreviews.data[action.selectedEmailId].default_text_preview = action.payload;
      newState.emailPreviews.data[action.selectedEmailId].preview_text = action.payload;
      newState.emailPreviews.textFetchSuccess = true;
      return newState;

    case AUTOMATION_WORKFLOW_EMAIL_PREVIEW.RESET:
      newState.emailPreviews.loading = false;
      newState.emailPreviews.data = {};
      return newState;

    case AUTOMATION_WORKFLOW_EMAIL_PREVIEW.RESET_SELECTED_EMAIL_ID:
      newState.emailPreviews.selectedEmailId = -1;
      newState.emailPreviews.selectedCampaignId = -1;
      newState.emailPreviews.textFetchSuccess = false;
      return newState;

    case AUTOMATION_WORKFLOW_TRIGGER_EMAIL.UPDATE:
      newState.workflow.detail.emailId = action.payload.emailId;
      newState.workflow.detail.emailName = action.payload.emailName;
      newState.workflow.detail.campaignId = action.payload.campaignId;
      return newState;

    case AUTOMATION_WORKFLOW_TRIGGER_LIST.UPDATE:
      newState.workflow.detail.listId = action.payload.listId;
      newState.workflow.detail.listName = action.payload.listName;
      return newState;

    // Workflow Clone
    case AUTOMATION_WORKFLOW_CLONE.LOAD:
      newState.userWorkflows.loading = true;
      newState.userWorkflows.error = false;
      return newState;

    case AUTOMATION_WORKFLOW_CLONE.LOAD_SUCCESS:
      newState.userWorkflows.loading = false;
      newState.userWorkflows.error = false;
      newState.userWorkflows.clonedWorkflowId = action.payload.workflowId;
      let obj = { ...action.payload.data, trigger_email_sent: false };
      let alreadyWorkflows: IUserWorkflowData[] = newState.userWorkflows.data;
      newState.userWorkflows.data = [obj, ...alreadyWorkflows];
      if(newState?.userWorkflows.filterType === 'draft' && !newState?.userWorkflows?.searchTerm.length){// handle the only filter without search
        newState.userWorkflows.searchedData = [obj, ... newState.userWorkflows.searchedData]
      }
      if(newState?.userWorkflows?.searchTerm.length){// handle search with filter case
        const searchedArray = [obj, ... newState.userWorkflows.searchedData];
        if(newState?.userWorkflows?.filterType){
          newState.userWorkflows.searchedData = searchedArray.filter(obj => obj.status === newState?.userWorkflows?.filterType )
        }else{
          newState.userWorkflows.searchedData = searchedArray
        }
      }
      return newState;

    case AUTOMATION_WORKFLOW_CLONE.LOAD_FAILED:
      newState.userWorkflows.loading = false;
      newState.userWorkflows.error = true;
      newState.userWorkflows.message = action.payload.message ? action.payload.message : '';
      return newState;

    case AUTOMATION_WORKFLOW_CLONE.RESET:
      newState.userWorkflows.loading = false;
      newState.userWorkflows.error = false;
      newState.userWorkflows.message = '';
      return newState;
    case AUTOMATION_EMAILS_ANALYTICS_LIST.LOAD: {
      newState.emailAnalytics.loading = true;
      return newState;
    }
    case AUTOMATION_EMAILS_ANALYTICS_LIST.SET_DATA: {
      const { emails, emailsCount } = action.payload;
      newState.emailAnalytics.loading = false;
      newState.emailAnalytics.data = emails.map((email: any) => ({
        ...email,
        subject: convertUnicodeInStringToEmojis(email.subject),
      }));
      newState.emailAnalytics.emailsCount = emailsCount;
      return newState;
    }
    case AUTOMATION_EMAILS_ANALYTICS_LIST.RESET_DATA: {
      newState.emailAnalytics.loading = false;
      newState.emailAnalytics.data = [];
      newState.emailAnalytics.emailsCount = 0;
      return newState;
    }
    case AUTOMATION_EMAILS_GRAPH_DATA.LOAD: {
      newState.workflowEmailGraph.loading = true;
      return newState;
    }
    case AUTOMATION_EMAILS_GRAPH_DATA.SET_DATA: {
      const { data } = action.payload;
      newState.workflowEmailGraph.loading = false;
      newState.workflowEmailGraph.data = data;
      return newState;
    }
    case AUTOMATION_EMAILS_GRAPH_DATA.RESET_DATA: {
      newState.workflowEmailGraph.loading = false;
      newState.workflowEmailGraph.data = {};
      return newState;
    }
    case AUTOMATION_EMAILS_GRAPH_DATA.SET_DATE: {
      newState.workflowEmailGraph.selectedDateFilter = action.payload.selectedDateFilter;
      newState.workflowEmailGraph.selectedDateValue.startDate = action.payload.selectedDateValue.start_date;
      newState.workflowEmailGraph.selectedDateValue.endDate = action.payload.selectedDateValue.end_date;
      return newState;
    }
    case AUTOMATION_EMAILS_GRAPH_DATA.RESET_DATE: {
      newState.workflowEmailGraph.selectedDateFilter = { label: '1 QTR', value: 'qtr' };
      newState.workflowEmailGraph.selectedDateValue.startDate = getQuarterDates().start_date;
      newState.workflowEmailGraph.selectedDateValue.endDate = getQuarterDates().end_date;
      return newState;
    }
    default:
      return newState;
  }
};
